import { VesselHistory } from '@/types/api';
import type { Map, LayerGroup } from 'leaflet';

import { create } from 'zustand';

interface OverlaysSettings {
  labels: boolean;
  seaMark: boolean;
  marineTraffic: boolean;
  windyMap: boolean;
  ports: boolean;
  showPortBySystem: boolean;
}

interface MeteorologySettinggs {
  clouds: boolean;
  precipitation: boolean;
  rain: boolean;
  pressure: boolean;
  pressureCountour: boolean;
  temperature: boolean;
  wind: boolean;
  temp: boolean;
}

interface MapsStore {
  maps: Map;
  canvasLayer: any;
  setMaps: (maps: Map) => void;
  setCanvasLayer: (canvasLayer: any) => void;
  savedCenter: [number, number];
  savedZoom: number;
  setSavedCenter: (center: [number, number]) => void;
  setSavedZoom: (zoom: number) => void;
  overlaysSettings: OverlaysSettings;
  meteorologySettings: MeteorologySettinggs;
  setOverlaysSettings: (key: string, value: boolean) => void;
  setMeteorologySettings: (key: string, value: boolean) => void;
  markersRouteRef: any[];
  setMarkersRouteRef: (markersRouteRef: any[]) => void;
  routesRef: L.LayerGroup<any> | null;
  setRoutsRef: (routesRef: L.LayerGroup<any> | null) => void;
  polylineRef: any[];
  setPolylineRef: (polylineRef: any[]) => void;
  polylineRouteData: any[];
  setPolylineRouteData: (polylineRouteData: any[]) => void;
  polylineAnimationRef: any[];
  setPolylineAnimationRef: (polylineRef: any[]) => void;
  vesselAnimationMarkerRef: L.Marker<any> | null;
  setVesselAnimationMarkerRef: (vesselAnimationMarkerRef: L.Marker<any> | null) => void;
  cleanRoutes: () => void;
  modalOpenVesselConfiguration: boolean;
  modalMinimizedVesselConfiguration: boolean;
  setModalOpenVesselConfiguration: (modalOpenVesselConfiguration: boolean) => void;
  setModalMinimizedVesselConfiguration: (modalMinimizedVesselConfiguration: boolean) => void;
  toggleModalOpenVesselConfiguration: () => void;
  isMapsLayerOpen: boolean;
  setMapsLayerOpen: (isOpen: boolean) => void;
  toggleMapsLayerOpen: () => void;
  modalMinimizedMapsLayer: boolean;
  setModalMinimizedMapsLayer: (modalMinimizedMapsLayer: boolean) => void;
  etaPolylineRef: any | null;
  setEtaPolylineRef: (ref: any | null) => void;
  etaHistoryPolylineRef: any | null;
  setEtaHistoryPolylineRef: (ref: any | null) => void;
  etaDestinationMarkerRef: any | null;
  setEtaDestinationMarkerRef: (ref: any | null) => void;
  etaOriginMarkerRef: any | null;
  setEtaOriginMarkerRef: (ref: any | null) => void;
  cleanEtaPredictionRoute: () => void;
}

const getInitialWindyMap = (): boolean => {
  return false;
};

const useMapsStore = create<MapsStore>((set, get) => ({
  //@ts-ignore
  maps: null,
  setMaps: (maps) => {
    console.log("useMapsStore: setMaps called with maps truthy?:", !!maps);
    set({ maps: maps });
  },
  canvasLayer: null,
  setCanvasLayer: (canvasLayer) => set({ canvasLayer: canvasLayer }),
  savedCenter: [-3.316694, 114.590111],
  savedZoom: 6,
  setSavedCenter: (center) => set({ savedCenter: center }),
  setSavedZoom: (zoom) => set({ savedZoom: zoom }),
  overlaysSettings: {
    labels: true,
    seaMark: false,
    marineTraffic: false,
    windyMap: getInitialWindyMap(),
    ports: false,
    showPortBySystem: false,
  },
  meteorologySettings: {
    clouds: false,
    precipitation: false,
    rain: false,
    pressure: false,
    pressureCountour: false,
    temperature: false,
    wind: false,
    temp: false,
  },
  setOverlaysSettings: (key, val) => {
    set((state) => ({
      overlaysSettings: {
        ...state.overlaysSettings,
        [key]: val,
      },
    }));
  },
  setMeteorologySettings: (key, val) => {
    set((state) => ({
      meteorologySettings: {
        ...state.meteorologySettings,
        [key]: val,
      },
    }));
  },

  /**
   * @description
   * Detail marker state
   */

  markersRouteRef: [],
  setMarkersRouteRef: (markersRouteRef) => set(() => ({ markersRouteRef: markersRouteRef })),
  routesRef: null,
  setRoutsRef: (routesRef) => set(() => ({ routesRef: routesRef })),
  polylineRef: [],
  setPolylineRef: (polylineRef) => set(() => ({ polylineRef: polylineRef })),
  polylineRouteData: [],
  setPolylineRouteData: (polylineRouteData) =>
    set(() => ({ polylineRouteData: polylineRouteData })),
  polylinAnimationRef: [],
  setPolylineAnimationRef: (polylineRef) => set(() => ({ polylineAnimationRef: polylineRef })),
  vesselAnimationMarkerRef: null,
  setVesselAnimationMarkerRef: (vesselAnimationMarkerRef) =>
    set(() => ({ vesselAnimationMarkerRef: vesselAnimationMarkerRef })),
  cleanRoutes: () => {
    const {
      maps,
      routesRef,
      polylineRef,
      markersRouteRef,
      polylineAnimationRef,
      vesselAnimationMarkerRef,
      setMarkersRouteRef,
      setPolylineRef,
      setPolylineRouteData,
      setRoutsRef,
      canvasLayer,
    } = get();

    if (!maps) return;

    const safeRemove = (ref: any) => {
      if (!ref) return;
      if (Array.isArray(ref)) {
        ref.forEach((item) => safeRemove(item));
      } else if (typeof ref === 'object') {
        if (typeof ref.remove === 'function') {
          try {
            ref.remove();
          } catch (e) { }
        } else if (typeof maps.removeLayer === 'function') {
          try {
            maps.removeLayer(ref);
          } catch (e) { }
        }
      } else if (typeof ref === 'string' && ref.trim() !== '') {
        try {
          if (typeof maps.removeLayer === 'function') (maps as any).removeLayer(ref);
        } catch (e) { }
      }
    };

    safeRemove(routesRef);
    safeRemove(polylineRef);
    safeRemove(polylineAnimationRef);
    safeRemove(vesselAnimationMarkerRef);

    // Clear ETA prediction route and marker
    const { etaPolylineRef, etaDestinationMarkerRef, setEtaPolylineRef, setEtaDestinationMarkerRef } = get();
    safeRemove(etaPolylineRef);
    safeRemove(etaDestinationMarkerRef);
    setEtaPolylineRef(null);
    setEtaDestinationMarkerRef(null);

    if (Array.isArray(markersRouteRef) && markersRouteRef.length > 0) {
      for (let i = 0; i < markersRouteRef.length; i++) {
        try {
          canvasLayer?.removeMarker?.(markersRouteRef[i]);
        } catch (e) {}
        try {
          markersRouteRef[i]?.remove?.();
        } catch (e) {}
        try {
          if (maps && typeof maps.removeLayer === 'function') {
            maps.removeLayer(markersRouteRef[i]);
          }
        } catch (e) {}
      }
    }
    setMarkersRouteRef([]);
    setPolylineRef([]);
    setPolylineRouteData([]);
    setRoutsRef(null);
  },
  etaPolylineRef: null,
  setEtaPolylineRef: (ref) => set({ etaPolylineRef: ref }),
  etaHistoryPolylineRef: null,
  setEtaHistoryPolylineRef: (ref) => set({ etaHistoryPolylineRef: ref }),
  etaDestinationMarkerRef: null,
  setEtaDestinationMarkerRef: (ref) => set({ etaDestinationMarkerRef: ref }),
  etaOriginMarkerRef: null,
  setEtaOriginMarkerRef: (ref) => set({ etaOriginMarkerRef: ref }),
  cleanEtaPredictionRoute: () => {
    const { maps, etaPolylineRef, etaHistoryPolylineRef, etaDestinationMarkerRef, etaOriginMarkerRef } = get();
    if (etaPolylineRef && typeof etaPolylineRef.remove === 'function') {
      try {
        etaPolylineRef.remove();
      } catch (e) {}
      set({ etaPolylineRef: null });
    }
    if (etaHistoryPolylineRef && typeof etaHistoryPolylineRef.remove === 'function') {
      try {
        etaHistoryPolylineRef.remove();
      } catch (e) {}
      set({ etaHistoryPolylineRef: null });
    }
    if (etaDestinationMarkerRef && typeof etaDestinationMarkerRef.remove === 'function') {
      try {
        etaDestinationMarkerRef.remove();
      } catch (e) {}
      set({ etaDestinationMarkerRef: null });
    }
    if (etaOriginMarkerRef && typeof etaOriginMarkerRef.remove === 'function') {
      try {
        etaOriginMarkerRef.remove();
      } catch (e) {}
      set({ etaOriginMarkerRef: null });
    }
  },
  modalMinimizedVesselConfiguration: false,
  setModalMinimizedVesselConfiguration: (modalMinimizedVesselConfiguration) =>
    set(() => ({ modalMinimizedVesselConfiguration })),
  modalOpenVesselConfiguration: false,
  setModalOpenVesselConfiguration: (modalOpenVesselConfiguration) =>
    set(() => ({ modalOpenVesselConfiguration })),
  toggleModalOpenVesselConfiguration: () =>
    set((state) => ({ modalOpenVesselConfiguration: !state.modalOpenVesselConfiguration })),
  isMapsLayerOpen: false,
  setMapsLayerOpen: (isOpen) => set(() => ({ isMapsLayerOpen: isOpen })),
  toggleMapsLayerOpen: () => set((state) => ({ isMapsLayerOpen: !state.isMapsLayerOpen })),
  modalMinimizedMapsLayer: false,
  setModalMinimizedMapsLayer: (modalMinimizedMapsLayer) =>
    set(() => ({ modalMinimizedMapsLayer })),
}));

export default useMapsStore;
